import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, User, Mail, Lock, Loader2, Check, AlertCircle, Trash2, FileText, Sparkles } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import UltimateTag from '../components/UltimateTag';
import UltimateTagModal from '../components/UltimateTagModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, checkAuth, logout } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savingBio, setSavingBio] = useState(false);
  const [savedBio, setSavedBio] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);

  const isGoogleUser = user?.auth_provider === 'google';

  const handleSaveName = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, { name: name.trim() }, { withCredentials: true });
      await checkAuth();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBio = async () => {
    setSavingBio(true);
    try {
      await axios.put(`${API_URL}/api/user/profile`, { bio: bio.trim() }, { withCredentials: true });
      await checkAuth();
      setSavedBio(true);
      setTimeout(() => setSavedBio(false), 2000);
    } catch (err) {
      console.error('Save bio error:', err);
    } finally {
      setSavingBio(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }
    setPasswordSaving(true);
    try {
      await axios.put(`${API_URL}/api/user/password`, { current_password: currentPassword, new_password: newPassword }, { withCredentials: true });
      setPasswordSaved(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordSaved(false), 2000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordSaving(false);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    setDeleteError('');
    if (deleteConfirmText.trim() !== 'DELETE') {
      setDeleteError('Type DELETE to confirm.');
      return;
    }
    if (!isGoogleUser && !deletePassword) {
      setDeleteError('Enter your password.');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/auth/account`, {
        withCredentials: true,
        data: {
          confirmation: deleteConfirmText.trim(),
          password: isGoogleUser ? undefined : deletePassword,
        },
      });
      await logout();
      navigate('/');
    } catch (err) {
      setDeleteError(err.response?.data?.detail || 'Failed to delete account');
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24">
      <div className="sticky top-0 z-40 bg-[#050505]/95 backdrop-blur-sm border-b border-white/5 px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate('/settings')} className="text-white" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="font-bold text-white">Account Settings</h1>
      </div>

      <div className="p-4 space-y-6">
        {/* Display Name */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-3">
          <Label className="text-[#A1A1AA] text-sm">Display Name</Label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 bg-[#0F1115] border-white/10 text-white rounded-lg"
                data-testid="name-input"
              />
            </div>
            <Button
              onClick={handleSaveName}
              disabled={saving || !name.trim() || name === user?.name}
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-lg px-4"
              data-testid="save-name-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : saved ? <Check className="w-4 h-4" /> : 'Save'}
            </Button>
          </div>
        </div>

        {/* Bio */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-3">
          <Label className="text-[#A1A1AA] text-sm">Bio</Label>
          <Textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="Tell people a little about yourself..."
            className="bg-[#0F1115] border-white/10 text-white rounded-lg min-h-[80px] resize-none"
            maxLength={300}
            data-testid="bio-input"
          />
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#71717A]">{bio.length}/300</span>
            <Button
              onClick={handleSaveBio}
              disabled={savingBio || bio === (user?.bio || '')}
              className="bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-lg px-4"
              data-testid="save-bio-btn"
            >
              {savingBio ? <Loader2 className="w-4 h-4 animate-spin" /> : savedBio ? <Check className="w-4 h-4" /> : 'Save'}
            </Button>
          </div>
        </div>

        {/* Email (read-only) */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-3">
          <Label className="text-[#A1A1AA] text-sm">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
            <Input
              value={user?.email || ''}
              disabled
              className="pl-10 bg-[#0F1115] border-white/10 text-[#71717A] rounded-lg cursor-not-allowed"
              data-testid="email-display"
            />
          </div>
          {isGoogleUser && (
            <p className="text-xs text-[#71717A] flex items-center gap-1">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Signed in with Google
            </p>
          )}
        </div>

        {/* Ultimate Custom Tag — Ultimate-only perk */}
        {user?.subscription_tier === 'ultimate' && (
          <div className="p-4 rounded-xl border border-pink-400/30 bg-gradient-to-br from-pink-500/[0.06] via-amber-500/[0.04] to-transparent space-y-3" data-testid="ultimate-tag-section">
            <div className="flex items-center justify-between">
              <Label className="text-pink-200 text-sm flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" /> Ultimate Tag
              </Label>
              <span className="text-[10px] uppercase tracking-wider font-bold text-pink-300/80">Ultimate</span>
            </div>
            <p className="text-xs text-white/60">
              A custom label shown next to your name everywhere on Animus. 1-4 words, changeable once a week.
            </p>
            <div className="flex items-center gap-2 flex-wrap min-h-[28px]">
              {user.ultimate_tag ? (
                <UltimateTag tag={user.ultimate_tag} />
              ) : (
                <span className="text-xs text-white/40 italic">No tag set yet</span>
              )}
            </div>
            <Button
              onClick={() => setShowTagModal(true)}
              className="bg-gradient-to-r from-[#F472B6] via-[#F59E0B] to-[#FCD34D] hover:opacity-90 text-[#2A0314] font-bold rounded-lg px-4"
              data-testid="open-tag-modal-btn"
            >
              {user.ultimate_tag ? 'Edit Tag' : 'Set Tag'}
            </Button>
          </div>
        )}

        {/* Change Password (only for email users) */}
        {!isGoogleUser && (
          <form onSubmit={handleChangePassword} className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-3">
            <Label className="text-[#A1A1AA] text-sm">Change Password</Label>
            {passwordError && (
              <div className="flex items-center gap-2 p-2 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-lg text-[#E11D48] text-xs" data-testid="password-error">
                <AlertCircle className="w-3 h-3" /><span>{passwordError}</span>
              </div>
            )}
            {passwordSaved && (
              <div className="flex items-center gap-2 p-2 bg-green-500/10 border border-green-500/20 rounded-lg text-green-500 text-xs">
                <Check className="w-3 h-3" /><span>Password changed successfully</span>
              </div>
            )}
            <div className="space-y-2">
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Current password"
                  className="pl-10 bg-[#0F1115] border-white/10 text-white rounded-lg"
                  data-testid="current-password-input"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New password"
                  className="pl-10 bg-[#0F1115] border-white/10 text-white rounded-lg"
                  data-testid="new-password-input"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="pl-10 bg-[#0F1115] border-white/10 text-white rounded-lg"
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={passwordSaving || !currentPassword || !newPassword}
              className="w-full bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-lg"
              data-testid="change-password-btn"
            >
              {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {passwordSaving ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        )}

        {/* Legal links */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-white/5 space-y-2" data-testid="legal-section">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-3.5 h-3.5 text-[#71717A]" />
            <h3 className="text-xs font-bold text-[#A1A1AA] uppercase tracking-wider">Legal</h3>
          </div>
          <button
            onClick={() => navigate('/privacy')}
            className="w-full text-left text-sm text-white hover:text-[#E11D48] py-1.5 transition-colors"
            data-testid="link-privacy-policy"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/terms')}
            className="w-full text-left text-sm text-white hover:text-[#E11D48] py-1.5 transition-colors"
            data-testid="link-terms"
          >
            Terms of Service
          </button>
        </div>

        {/* Danger Zone */}
        <div className="p-4 bg-[#1A1D24] rounded-xl border border-[#E11D48]/20 space-y-3">
          <h3 className="text-sm font-bold text-[#E11D48]">Danger Zone</h3>
          <p className="text-xs text-[#71717A]">Permanently delete your account and all associated data. This action cannot be undone.</p>
          <Button
            onClick={() => { setShowDeleteModal(true); setDeleteConfirmText(''); setDeletePassword(''); setDeleteError(''); }}
            disabled={deleting}
            variant="outline"
            className="w-full border-[#E11D48]/30 text-[#E11D48] hover:bg-[#E11D48]/10 rounded-lg"
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Account
          </Button>
        </div>
      </div>

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div className="w-full max-w-md bg-[#0F1115] border border-[#E11D48]/30 rounded-2xl p-5 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[#E11D48]/15 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-[#E11D48]" />
              </div>
              <div>
                <h3 className="font-['Unbounded'] text-base text-white">Delete account?</h3>
                <p className="text-xs text-[#A1A1AA] mt-1">This permanently deletes your account, characters, personas, chats, and all data. This cannot be undone.</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#A1A1AA] text-xs">Type <span className="font-bold text-[#E11D48]">DELETE</span> to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                className="bg-[#1A1D24] border-white/10 text-white rounded-lg"
                data-testid="delete-confirm-input"
              />
            </div>

            {!isGoogleUser && (
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs">Your password</Label>
                <Input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-[#1A1D24] border-white/10 text-white rounded-lg"
                  data-testid="delete-password-input"
                />
              </div>
            )}

            {deleteError && (
              <div className="flex items-center gap-2 p-2 bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-lg text-[#E11D48] text-xs" data-testid="delete-error">
                <AlertCircle className="w-3 h-3" /><span>{deleteError}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={() => setShowDeleteModal(false)}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-lg"
                disabled={deleting}
                data-testid="delete-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 bg-[#E11D48] hover:bg-[#BE123C] text-white rounded-lg"
                data-testid="delete-confirm-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {deleting ? 'Deleting...' : 'Delete forever'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Ultimate Tag Modal */}
      {showTagModal && (
        <UltimateTagModal
          currentTag={user?.ultimate_tag}
          onClose={() => setShowTagModal(false)}
          onSaved={() => checkAuth()}
        />
      )}
    </div>
  );
}
